import { Record, Map, List } from 'immutable';
import { fetchFormActions, addFormActions, updateFormActions, actionTypes,
  fetchTouchpointInfoActions, fetchTouchpointActions, fetchValidTouchpointsActions, fetchProgramDemographicsActions,
  fetchCurrentFormPreviewActions } from './modify-intake-form.actions';
import { publishActions, retractActions } from '../publish-intake-form/publish-intake-form.actions';
import * as api from '../../api';
import * as intakeFormConstants from './intake-form-constants';

/**
 * @property {api.ApiCallState} currentEditPreview used to hold the generated form preview of an intake form being edited
 * @property {string} siteId guid for the site associated with the intake form to preview
 * @property {string} programId guid for the program associated with the intake form to preview
 * @property {bool} disableReCaptcha should recaptcha be shown in preview
 * @property {bool} inProgress whether or not the form is in the middle of being created/updated
 * @property {*} formValue if the intake form is being edited, the form value (newValue/editValue) should be copied here
 */
export const PreviewState = Record ({
  currentEditPreview: api.ApiCallState({}),
  siteId: null,
  programId: null,
  disableReCaptcha: false,
  inProgress: false,
  formValue: null,
})

//TODO: use modify state to simplify ModifyIntakeFormState (putting off for now to avoid merge hell)
/**
 * @property {api.ApiCallState} saveState represents the saving process
 * @property {Map<string, any>} values the values that have been entered
 * @property {Array} validation a list of validation errors for a new intake form
 * @property {boolean} showValidation determines if a list of validation errors should show in the ui
 * @property {string} selectedTouchpoint currently selected touchpoint
 */
export const ModifyState = Record({
  saveState: api.ApiCallState({}),
  values: Map({}),
  validation: [],
  showValidation: false,
  selectedTouchpoint: null,
});

export const EditQuestionState = Record ({
  showQuestionEditModal: false,
  questionEditModalQuestionId: null,
  questionEditModalIntakeFormId: null,
  label: '',
  tooltip: '',
});

/**
 * @property {Map<string, api.ApiCallState>} intakeForms intake forms indexed by the id of the intake form
 * @property {api.ApiCallState} addFormState represents the saving process for a new intake form
 * @property {Map<string, api.ApiCallState>} updateFormState represents the saving process for intake forms that are being edited, indexed by intake form id
 * @property {Map<string, Map<string, any>>} editValues the values that have been entered on edited intake forms, indexed by intake form id
 * @property {Map<string, any>} newValue the values that have been entered on a new intake form
 * @property {Map<string, Array>} editValuesValidation lists of validation errors for edited intake forms, indexed by intake form id
 * @property {Array} newValueValidation a list of validation errors for a new intake form
 * @property {boolean} showAddValidation determines if a list of validation errors should show in the add intake form ui
 * @property {Map<string, boolean>} showEditValidation booleans that determines if a list of validation errors should show in the edit intake form ui, indexed by intake form id
 * @property {api.ApiCallState} touchpointInfo touchpoint name information
 * @property {Map<string, api.ApiCallState>} touchpoints full touchpoint information, indexed by touchpoint form id
 * @property {Map<string, Map<string, api.ApiCallState>>} validIntakeTouchpoints lists of valid touchpoint form ids, indexed by siteId and programId
 * @property {string} selectedAddTouchpoint currently selected touchpoint for the add intake form ui
 * @property {Map<string, string>} selectedEditTouchpoint currently selected touchpoint for the edit intake form ui, indexed by intake form id
 * @property {Map<string, Map<string, api.ApiCallState>>} programDemographics all of the demographic questions available to use for a particular program (the one for which the form is being modified), indexed by siteId and programId
 * @property {api.ApiCallState} previewModifiedFormState
 * @property {Map<string, boolean>} showModal determines if the confirm modal should be showing for an edit page, indexed by intake form id
 * @property {boolean} preserveEdits determines if edits made to an intake form should be preserved, like when previewing a form
 * @property {string} intakePreviewRedirect used to preserve correct tab a user is on (if applicable) when editing or creating an intake form
 */
export const ModifyIntakeFormState = Record({
  intakeForms : Map({}),
  addFormState: api.ApiCallState({}),
  updateFormState: Map({}),
  editValues: Map({}),
  newValue: Map({}),
  editValuesValidation: Map({}),
  newValueValidation: [],
  showAddValidation: false,
  showEditValidation: Map({}),
  touchpointInfo: api.ApiCallState({}),
  touchpoints: Map({}),
  validIntakeTouchpoints: Map({}),
  selectedAddTouchpoint: null,
  selectedEditTouchpoint: Map({}),
  programDemographics: Map({}),
  previewModifiedFormState: PreviewState({}),
  showModal: Map({}),
  preserveEdits: false,
  intakePreviewRedirect: ``,
  editQuestion: EditQuestionState({}),
  radioButtonValue: 'specific'
});

export const modifyIntakeFormReducer = (initialState = new ModifyIntakeFormState(), action) => {

  var state = initialState;
  var invalidateActions = [updateFormActions.success];
  if(action.id) {
    state = state.setIn(['intakeForms', action.id],
      api.apiCallReducer(state.getIn(['intakeForms', action.id]), action, fetchFormActions, invalidateActions));

    state = state.setIn(['updateFormState', action.id],
      api.apiCallReducer(state.getIn(['updateFormState', action.id]), action, updateFormActions));
  }

  if(action.touchpointId) {
    state = state.setIn(['touchpoints', action.touchpointId],
      api.apiCallReducer(state.getIn(['touchpoints', action.touchpointId]), action, fetchTouchpointActions));
  }

  state = state.set('addFormState',
    api.apiCallReducer(state.get('addFormState'), action, addFormActions));

  state = state.set('touchpointInfo',
    api.apiCallReducer(state.get('touchpointInfo'), action, fetchTouchpointInfoActions));

  if(!!action.siteId && !!action.programId) {
    var path = ['validIntakeTouchpoints', action.siteId, action.programId];
    state = state.setIn(path,
      api.apiCallReducer(state.getIn(path), action, fetchValidTouchpointsActions));

    path = ['programDemographics', action.siteId, action.programId];
    state = state.setIn(path,
      api.apiCallReducer(state.getIn(path), action, fetchProgramDemographicsActions));
  }

  state = state.setIn(['previewModifiedFormState', 'currentEditPreview'],
    api.apiCallReducer(state.getIn(['previewModifiedFormState', 'currentEditPreview']), action, fetchCurrentFormPreviewActions));

  switch (action.type) {
    case publishActions.success:
    case retractActions.success:
      return state.setIn(['intakeForms', action.id, 'data'], action.data);
    case actionTypes.CLEAR_ANSWERS:
      return state.set('newValue', new Map())
                  .set('showAddValidation', false)
                  .set('newValueValidation', [])
                  .set('selectedAddTouchpoint', null)
                  .set('editValues', new Map())
                  .set('showEditValidation', new Map())
                  .set('editValuesValidation', new Map())
                  .set('selectedEditTouchpoint', new Map())
                  .set('previewModifiedFormState', new PreviewState());
    case actionTypes.PRESERVE_EDITS:
      return state.set('preserveEdits', action.preserve);
    case addFormActions.success:
      return state.set('newValue', new Map())
                  .set('showAddValidation', false);
    case updateFormActions.success:
      return state.deleteIn(['editValues', action.id])
                  .setIn(['showEditValidation', action.id], false);
    case actionTypes.UPDATE_VALUE:
      return state.setIn(action.path, action.value);
    case actionTypes.UPDATE_PREVIEW_STATE:
      return state.setIn(['previewModifiedFormState', 'siteId'], action.siteId)
                  .setIn(['previewModifiedFormState', 'programId'], action.programId)
                  .setIn(['previewModifiedFormState', 'disableReCaptcha'], action.disableReCaptcha)
                  .setIn(['previewModifiedFormState', 'inProgress'], action.inProgress)
                  .setIn(['previewModifiedFormState', 'formValue'], action.value);
    case actionTypes.SELECT_ADD_TOUCHPOINT:
      if(!action.touchpointId) {
        state = state.setIn(['newValue', intakeFormConstants.touchpointSection], List([]));
      }
      return state.set('selectedAddTouchpoint', action.touchpointId);
    case actionTypes.SELECT_EDIT_TOUCHPOINT:
      if(!action.touchpointId) {
        state = state.setIn(['editValues', action.id, intakeFormConstants.touchpointSection], List([]));
      }
      return state.setIn(['selectedEditTouchpoint', action.id], action.touchpointId);
    case actionTypes.UPDATE_VALIDATION:
      return state.setIn(action.path, action.value);
    case actionTypes.SHOW_ADD_VALIDATION:
      return state.set('showAddValidation', true);
    case actionTypes.SHOW_EDIT_VALIDATION:
      return state.setIn(['showEditValidation', action.id], true);
    case actionTypes.SHOW_MODAL:
      return state.setIn(['showModal', action.id], action.show);
    case actionTypes.SHOW_DEMO_QUESTION_EDIT_MODAL_EDIT_INTAKE:
      //check if there is an pending (not saved) update to label and/or tooltip
      const demoEditValues = state.getIn(['editValues', action.intakeFormId, intakeFormConstants.demographicsSection]);
      const demoEditQuestion = demoEditValues.find(val => val.get('demographicId') === action.demographicId);
      const demoUpdateCustomLabel = demoEditQuestion && demoEditQuestion.get('label');
      const demoUpdateCustomTooltip = demoEditQuestion && demoEditQuestion.get('tooltip');

      //get existing label and tooltip and set as default
      const demoIntakeForms = state.get('intakeForms');
      const demoIntakeForm = demoIntakeForms && demoIntakeForms.get(action.intakeFormId);
      const demoIntakeFormElements = demoIntakeForm && demoIntakeForm.data && demoIntakeForm.data.demographicQuestions;
      const demoSelectedIntakeElement = demoIntakeFormElements.find(val => val.demographicId === action.demographicId);
      const demoSelectedCustomLabel = demoSelectedIntakeElement && demoSelectedIntakeElement.label;
      const demoSelectedCustomTooltip = demoSelectedIntakeElement && demoSelectedIntakeElement.tooltip;
      const demoDefaultCustomLabel = demoUpdateCustomLabel || demoSelectedCustomLabel;
      const demoDefaultCustomTooltip = demoUpdateCustomTooltip || demoSelectedCustomTooltip;
      return state.setIn(['editQuestion', 'showQuestionEditModal'], true)
                  .setIn(['editQuestion', 'questionEditModalIntakeFormId'], action.intakeFormId)
                  .setIn(['editQuestion', 'questionEditModalQuestionId'], action.demographicId)
                  .setIn(['editQuestion', 'label'], demoDefaultCustomLabel)
                  .setIn(['editQuestion', 'tooltip'], demoDefaultCustomTooltip);
    case actionTypes.SHOW_DEMO_QUESTION_EDIT_MODAL_CREATE_INTAKE:
      const demoEditValuesCreate = state.getIn(['newValue', intakeFormConstants.demographicsSection]);
      const demoEditQuestionCreate = demoEditValuesCreate.find(val => val.get('demographicId') === action.demographicId);
      const demoUpdateCustomLabelCreate = demoEditQuestionCreate && demoEditQuestionCreate.get('label');
      const demoUpdateCustomTooltipCreate = demoEditQuestionCreate && demoEditQuestionCreate.get('tooltip');
      return state.setIn(['editQuestion', 'showQuestionEditModal'], true)
                  .setIn(['editQuestion', 'questionEditModalIntakeFormId'], action.intakeFormId)
                  .setIn(['editQuestion', 'questionEditModalQuestionId'], action.demographicId)
                  .setIn(['editQuestion', 'label'], demoUpdateCustomLabelCreate)
                  .setIn(['editQuestion', 'tooltip'], demoUpdateCustomTooltipCreate);
    case actionTypes.CLOSE_DEMO_QUESTION_EDIT_MODAL:
      return state.setIn(['editQuestion', 'showQuestionEditModal'], false)
                  .setIn(['editQuestion', 'questionEditModalQuestionId'], null)
                  .setIn(['editQuestion', 'label'], null)
                  .setIn(['editQuestion', 'tooltip'], null);
    case actionTypes.SHOW_TOUCHPOINT_QUESTION_EDIT_MODAL_EDIT_INTAKE:
      //check if there is an pending (not saved) update to label and/or tooltip
      const touchpointEditValues = state.getIn(['editValues', action.intakeFormId, intakeFormConstants.touchpointSection]);
      const touchpointEditQuestion = touchpointEditValues.find(val => val.get('elementId') === action.touchpointId);
      const touchpointUpdateCustomLabel = touchpointEditQuestion && touchpointEditQuestion.get('label');
      const touchpointUpdateCustomTooltip = touchpointEditQuestion && touchpointEditQuestion.get('tooltip');

      //get existing label and tooltip and set as default
      const touchpointIntakeForms = state.get('intakeForms');
      const touchpointIntakeForm = touchpointIntakeForms && touchpointIntakeForms.get(action.intakeFormId);
      const touchpointIntakeFormElements = touchpointIntakeForm && touchpointIntakeForm.data && touchpointIntakeForm.data.touchpointQuestions;
      const touchpointSelectedIntakeElement = touchpointIntakeFormElements.find(val => val.elementId === action.touchpointId);
      const touchpointSelectedCustomLabel = touchpointSelectedIntakeElement && touchpointSelectedIntakeElement.label;
      const touchpointSelectedCustomTooltip = touchpointSelectedIntakeElement && touchpointSelectedIntakeElement.tooltip;
      const touchpointDefaultCustomLabel = touchpointUpdateCustomLabel || touchpointSelectedCustomLabel;
      const touchpointDefaultCustomTooltip = touchpointUpdateCustomTooltip || touchpointSelectedCustomTooltip;
      return state.setIn(['editQuestion', 'showQuestionEditModal'], true)
                  .setIn(['editQuestion', 'questionEditModalIntakeFormId'], action.intakeFormId)
                  .setIn(['editQuestion', 'questionEditModalQuestionId'], action.touchpointId)
                  .setIn(['editQuestion', 'label'], touchpointDefaultCustomLabel)
                  .setIn(['editQuestion', 'tooltip'], touchpointDefaultCustomTooltip);
    case actionTypes.SHOW_TOUCHPOINT_QUESTION_EDIT_MODAL_CREATE_INTAKE:
      const touchpointEditValuesCreate = state.getIn(['newValue', intakeFormConstants.touchpointSection]);
      const touchpointEditQuestionCreate = touchpointEditValuesCreate.find(val => val.get('elementId') === action.touchpointId);
      const touchpointUpdateCustomLabelCreate = touchpointEditQuestionCreate && touchpointEditQuestionCreate.get('label');
      const touchpointUpdateCustomTooltipCreate = touchpointEditQuestionCreate && touchpointEditQuestionCreate.get('tooltip');
      return state.setIn(['editQuestion', 'showQuestionEditModal'], true)
                  .setIn(['editQuestion', 'questionEditModalIntakeFormId'], action.intakeFormId)
                  .setIn(['editQuestion', 'questionEditModalQuestionId'], action.touchpointId)
                  .setIn(['editQuestion', 'label'], touchpointUpdateCustomLabelCreate)
                  .setIn(['editQuestion', 'tooltip'], touchpointUpdateCustomTooltipCreate);
    case actionTypes.CLOSE_TOUCHPOINT_QUESTION_EDIT_MODAL:
      return state.setIn(['editQuestion', 'showQuestionEditModal'], false)
                  .setIn(['editQuestion', 'questionEditModalQuestionId'], null)
                  .setIn(['editQuestion', 'label'], null)
                  .setIn(['editQuestion', 'tooltip'], null);
    case actionTypes.UPDATE_PREVIEW_TAB_REDIRECT:
      return state.set('intakePreviewRedirect', action.tab);
    case actionTypes.UPDATE_CUSTOM_LABEL:
      return state.setIn(['editQuestion', 'label'], action.label);
    case actionTypes.UPDATE_CUSTOM_TOOLTIP:
      return state.setIn(['editQuestion', 'tooltip'], action.tooltip);
    // get current radio button value of modal and set as default
    case actionTypes.SET_SELECTED_SAVE_OPTION:
      return state.set('radioButtonValue', action.value);  
    default:
      return state
  }
}
